import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Heart, CheckCircle, Loader2, XCircle, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

import API_URL from '../config/api';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { t, ready } = useTranslation();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('polling'); // polling | success | expired | error
  const [plan, setPlan] = useState('');
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`, {
        withCredentials: true
      });

      const { payment_status, plan: upgradedPlan } = response.data;
      setPlan(upgradedPlan);

      if (payment_status === 'paid') {
        setStatus('success');
        // Refresh user data to reflect new subscription
        if (refreshUser) await refreshUser();
        return;
      } else if (payment_status === 'expired' || response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      if (attempts < 10) {
        setAttempts(prev => prev + 1);
      } else {
        setStatus('error');
      }
    } catch {
      if (attempts < 5) {
        setAttempts(prev => prev + 1);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, attempts, refreshUser]);

  useEffect(() => {
    if (status !== 'polling') return;

    const timer = setTimeout(pollStatus, attempts === 0 ? 500 : 2000);
    return () => clearTimeout(timer);
  }, [status, attempts, pollStatus]);

  if (!ready) return null;

  const planName = plan === 'gold' ? 'Gold' : 'Premium';
  const planColor = plan === 'gold' ? '#D4AF37' : '#6E1A24';

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === 'polling' && (
          <div className="space-y-6" data-testid="payment-polling">
            <div className="w-20 h-20 rounded-full bg-[#6E1A24]/10 flex items-center justify-center mx-auto">
              <Loader2 className="w-10 h-10 text-[#6E1A24] animate-spin" />
            </div>
            <h1 className="text-3xl font-serif font-light text-[#2D2D2A]">
              {t('payment.processing_title', 'Processing your payment...')}
            </h1>
            <p className="text-[#6C6C67]">
              {t('payment.processing_desc', 'Please wait while we confirm your payment.')}
            </p>
            <div className="flex justify-center gap-1.5 pt-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-2.5 h-2.5 rounded-full bg-[#6E1A24]/30 animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />
              ))}
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6" data-testid="payment-success">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: `${planColor}15` }}>
              {plan === 'gold' ? (
                <Crown className="w-10 h-10" style={{ color: planColor }} />
              ) : (
                <CheckCircle className="w-10 h-10" style={{ color: planColor }} />
              )}
            </div>
            <h1 className="text-3xl font-serif font-light text-[#2D2D2A]">
              {t('payment.success_title', 'Welcome to {{plan}}!', { plan: planName })}
            </h1>
            <p className="text-[#6C6C67] max-w-sm mx-auto">
              {t('payment.success_desc', 'Your upgrade to {{plan}} is now active. Enjoy unlimited likes, see who liked you, and more.', { plan: planName })}
            </p>
            <div className="flex flex-col gap-3 pt-4">
              <Button
                onClick={() => navigate('/discover')}
                className="rounded-full py-3.5 text-base font-semibold text-white"
                style={{ backgroundColor: planColor }}
                data-testid="payment-go-discover"
              >
                {t('payment.start_discovering', 'Start Discovering')}
              </Button>
              <Link to="/settings" className="text-sm text-[#6C6C67] hover:text-[#2D2D2A] transition-colors">
                {t('payment.view_subscription', 'View Subscription Settings')}
              </Link>
            </div>
          </div>
        )}

        {status === 'expired' && (
          <div className="space-y-6" data-testid="payment-expired">
            <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-amber-500" />
            </div>
            <h1 className="text-3xl font-serif font-light text-[#2D2D2A]">
              {t('payment.expired_title', 'Payment Session Expired')}
            </h1>
            <p className="text-[#6C6C67]">
              {t('payment.expired_desc', 'Your payment session has expired. Please try again.')}
            </p>
            <Button
              onClick={() => navigate('/settings')}
              className="bg-[#6E1A24] text-white rounded-full px-8 py-3 font-semibold hover:bg-[#8A2331] transition-all"
              data-testid="payment-retry"
            >
              {t('payment.try_again', 'Try Again')}
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6" data-testid="payment-error">
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-3xl font-serif font-light text-[#2D2D2A]">
              {t('payment.error_title', 'Something went wrong')}
            </h1>
            <p className="text-[#6C6C67]">
              {t('payment.error_desc', "We couldn't verify your payment. If you were charged, your subscription will be activated shortly.")}
            </p>
            <div className="flex flex-col gap-3 pt-4">
              <Button
                onClick={() => navigate('/settings')}
                className="bg-[#6E1A24] text-white rounded-full px-8 py-3 font-semibold hover:bg-[#8A2331] transition-all"
                data-testid="payment-go-settings"
              >
                {t('payment.go_to_settings', 'Go to Settings')}
              </Button>
              <Link to="/contact" className="text-sm text-[#6C6C67] hover:text-[#2D2D2A] transition-colors">
                {t('payment.contact_support', 'Contact Support')}
              </Link>
            </div>
          </div>
        )}

        {/* Footer branding */}
        <div className="mt-12 flex items-center justify-center gap-2 text-[#9C9C97]">
          <Heart className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm font-serif">LusoLove</span>
        </div>
      </div>
    </div>
  );
}
